/**
 * src/config/contentPlannerLaunch.js
 *
 * CONTENT PLANNER LAUNCH STATE — server-authoritative, fail closed.
 *
 * Content Planner is not commercially launched. VISINVOKE deliberately shows
 * what is coming (the tiers and their intended prices stay visible), but there
 * must be no working purchase path until the owner explicitly enables sales on
 * the server.
 *
 * The server decides. This module only reads GET /v1/content-planner/commerce
 * and hands the answer to the surfaces that present the product, so no screen
 * carries its own opinion about whether Content Planner is on sale.
 *
 * Fail closed on every branch: while loading, on a network error, on a
 * malformed payload, or on any missing field, `purchasable` is false and the
 * customer sees "Not Available Yet". Configuring Stripe price ids cannot open
 * the path — the server gate is separate, and this reads the gate.
 */
import { useEffect, useState } from 'react';
import API from '../api';

// The state used before the server answers, and whenever it cannot be trusted.
export const CONTENT_PLANNER_PRELAUNCH = {
  loaded: false,
  launchState: 'prelaunch',
  salesEnabled: false,
  purchasable: false,
  statusLabel: 'Not Available Yet',
  customerMessage:
    'Content Planner is not available yet. Pricing shown is the intended launch pricing and may change before release.',
};

function normalise(payload) {
  if (!payload || typeof payload !== 'object') return CONTENT_PLANNER_PRELAUNCH;
  // Only an explicit server `true` makes the product purchasable.
  const purchasable = payload.purchasable === true && payload.sales_enabled === true;
  return {
    loaded: true,
    launchState: typeof payload.launch_state === 'string' ? payload.launch_state : 'prelaunch',
    salesEnabled: payload.sales_enabled === true,
    purchasable,
    statusLabel:
      typeof payload.status_label === 'string' && payload.status_label
        ? payload.status_label
        : CONTENT_PLANNER_PRELAUNCH.statusLabel,
    customerMessage:
      typeof payload.customer_message === 'string' && payload.customer_message
        ? payload.customer_message
        : CONTENT_PLANNER_PRELAUNCH.customerMessage,
  };
}

/**
 * Read the authoritative Content Planner launch state.
 * Returns the pre-launch shape until (and unless) the server says otherwise.
 */
export function useContentPlannerLaunch() {
  const [state, setState] = useState(CONTENT_PLANNER_PRELAUNCH);

  useEffect(() => {
    let cancelled = false;
    API.get('/v1/content-planner/commerce')
      .then((res) => {
        if (!cancelled) setState(normalise(res && res.data));
      })
      .catch(() => {
        // Unreachable / errored -> stay pre-launch. Never assume availability.
        if (!cancelled) setState(CONTENT_PLANNER_PRELAUNCH);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

export default useContentPlannerLaunch;
